import { useQuery } from "@apollo/client";
import { PERSON_BY_ID_QUERY } from "@/api/person/queries";
import type { DispensationEvidenceModel } from "@/api/cle-api";
import { CleApiErrorText } from "@/components/cle-api/CleApiErrorText";
import {
  mergeEvidenceDetailUpdate,
  mergeEvidenceDetailWithOverride,
  useDispensation,
  useDispensationPdf,
  useDownloadDispensation,
} from "@/hooks/cle-hooks";
import type { PersonDto } from "@/interfaces/schema";
import { transformQueryDataPerioder } from "@/util/apollo";
import { useEffect, useMemo, useState } from "react";

import {
  decoratePersonEfteruddannelseList,
  type DecoratedPersonEfteruddannelse,
} from "../person/id/Efteruddannelse/decoratePeriod";
import { EvidencePdfViewer } from "../person/id/Efteruddannelse/EvidencePdfViewer";
import { buildPeriodLabelLookup } from "../person/id/Efteruddannelse/dispensationUtils";
import { formatDate } from "../person/id/Efteruddannelse/utils/formatters";
import {
  formatEvidenceStatus,
  getEvidenceStatusVariant,
} from "../person/id/Efteruddannelse/utils/evidenceStatus";
import { StatusBadge } from "../person/id/Efteruddannelse/StatusBadge";
import { useObjectUrl } from "../person/id/Efteruddannelse/useObjectUrl";
import {
  DocumentAuditTrailPanel,
  DocumentCommentsPanel,
} from "./DocumentReviewPanels";
import { EvidenceReviewThreadTopPanel } from "./EvidenceReviewThreadTopPanel";
import { DispensationValidation } from "./DispensationValidation";
import { ReviewDetailLayout } from "./ReviewDetailLayout";
import { ReviewInfoCard, ReviewInfoField } from "./ReviewInfoCard";

interface DispensationReviewViewProps {
  userId: string;
  dispensationId: string;
  periods?: DecoratedPersonEfteruddannelse[];
  onBack: () => void;
}

export const DispensationReviewView = ({
  userId,
  dispensationId,
  periods = [],
  onBack,
}: DispensationReviewViewProps) => {
  const hasProvidedPeriods = periods.length > 0;
  const { data: personData } = useQuery<{ person: PersonDto }>(
    PERSON_BY_ID_QUERY("dispensationReviewPeriods", {
      personEfteruddannelse: true,
    }),
    {
      variables: { id: userId },
      skip: hasProvidedPeriods,
    },
  );

  const resolvedPeriods = useMemo(() => {
    if (hasProvidedPeriods) return periods;

    const transformedData = personData
      ? transformQueryDataPerioder(personData)
      : undefined;

    return decoratePersonEfteruddannelseList(
      transformedData?.person?.personEfteruddannelse,
    );
  }, [hasProvidedPeriods, periods, personData]);

  const { data, isLoading, error } = useDispensation({
    userId,
    dispensationId,
  });
  const { data: pdfBlob, isLoading: isPdfLoading } = useDispensationPdf({
    userId,
    dispensationId,
  });
  const { mutate: downloadDispensation, isPending: isDownloading } =
    useDownloadDispensation();
  const pdfUrl = useObjectUrl(pdfBlob);
  const [dispensationOverride, setDispensationOverride] = useState<
    DispensationEvidenceModel | undefined
  >(undefined);

  const periodLabelLookup = useMemo(
    () => buildPeriodLabelLookup(resolvedPeriods),
    [resolvedPeriods],
  );

  useEffect(() => {
    setDispensationOverride(undefined);
  }, [dispensationId]);

  const dispensation = useMemo(
    () => mergeEvidenceDetailWithOverride(data, dispensationOverride),
    [data, dispensationOverride],
  );

  const handleDispensationUpdated = (updated: DispensationEvidenceModel) => {
    setDispensationOverride((prev) =>
      mergeEvidenceDetailUpdate(data ?? prev, updated),
    );
  };

  return (
    <ReviewDetailLayout
      className="p-2 pb-4"
      onBack={onBack}
      topPanel={
        <EvidenceReviewThreadTopPanel
          reviewThread={dispensation?.reviewThread}
          ownerUserId={userId}
        />
      }
      loading={isLoading}
      error={
        error ? (
          <CleApiErrorText
            error={error}
            fallback="Kunne ikke hente dispensation."
          />
        ) : undefined
      }
      pdfPanel={
        dispensation ? (
          <EvidencePdfViewer
            fileName={dispensation.fileName ?? "Dispensation"}
            isDownloading={isDownloading}
            isLoading={isPdfLoading}
            onDownload={() => downloadDispensation({ userId, dispensationId })}
            pdfUrl={pdfUrl}
          />
        ) : null
      }
      auditPanel={<DocumentAuditTrailPanel subjectId={dispensationId} />}
      infoPanel={
        dispensation ? (
          <>
            <ReviewInfoCard title="Dispensationsdokument">
              <ReviewInfoField
                label="Status"
                value={
                  <StatusBadge
                    pill
                    status={formatEvidenceStatus(dispensation.status)}
                    variant={getEvidenceStatusVariant(dispensation.status)}
                  />
                }
              />
              <ReviewInfoField
                label="Indberettet"
                value={dispensation.isReported ? "Ja" : "Nej"}
              />
              <ReviewInfoField
                label="Oprettet"
                value={formatDate(dispensation.createdAt)}
              />
              <ReviewInfoField
                label="Oprettet af"
                value={dispensation.createdByUserName ?? "-"}
              />
            </ReviewInfoCard>
            <DispensationValidation
              dispensation={dispensation}
              periods={resolvedPeriods}
              periodLabelLookup={periodLabelLookup}
              userId={userId}
              onDispensationUpdated={handleDispensationUpdated}
            />
          </>
        ) : null
      }
      commentsPanel={
        <DocumentCommentsPanel
          subjectId={dispensationId}
          ownerUserId={userId}
          onCommentAdded={() => setDispensationOverride(undefined)}
          isReported={dispensation?.isReported ?? false}
        />
      }
    />
  );
};
